import React, { useState, useContext, useEffect } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { useCustomFetch } from '../../../hooks/useCustomFetch'
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select'
import { DoorOpen, Calendar, User, Mail } from 'lucide-react'
import toast from 'react-hot-toast'
import { DashboardContext } from '@/context/DashboardContext'

interface MeetingJoinPreference {
  value: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

interface SettingsProps {
  workspaceId?: string; // Optional if you want to allow null initially
}

export function Settings() {
  const { preferencesLoading, setPreferencesLoading, meetingJoinPreference, handleJoinPreference, activeWorkspace } = useContext(DashboardContext)
  
  const meetingJoinOptions: MeetingJoinPreference[] = [
    { 
      value: 'external', 
      label: 'Join External Meetings', 
      description: 'Only join and record meetings with people outside your organization',
      icon: <DoorOpen className="h-4 w-4 text-gray-500 mr-2" />
    },
    { 
      value: 'every', 
      label: 'Join Every Meeting', 
      description: 'Join and record all meetings on your calendar',
      icon: <Calendar className="h-4 w-4 text-gray-500 mr-2" />
    },
    { 
      value: 'created', 
      label: 'Join All Meetings I Create', 
      description: 'Only join meetings where you are the organizer',
      icon: <User className="h-4 w-4 text-gray-500 mr-2" />
    },
    { 
      value: 'invited', 
      label: 'Join Only When Invited', 
      description: 'Only join when Usermuse is explicitly added as a participant',
      icon: <Mail className="h-4 w-4 text-gray-500 mr-2" />
    }
  ]
  
  const handleMeetingJoinPreferenceChange = async (value: string) => {
    setPreferencesLoading(true)
    try {
      toast.success('Preferences updated successfully')
      await handleJoinPreference(value)
    } catch (error) {
      console.error('Error updating preferences', error)
      toast.error('Failed to update preferences')
    } finally {
      setPreferencesLoading(false)
    }
  }

  // Get the current selected option for the trigger display
  const getSelectedOption = () => {
    return meetingJoinOptions.find(option => option.value === meetingJoinPreference);
  }
  
  return (
    <div className="pr-[102px] pt-[36px]">
      <div className="mb-[36px]">
        <p className="text-[#40424A] dark:text-gray-100 text-[22px] font-medium font-['DM_Sans'] leading-relaxed">Settings</p>
        <p className="text-slate dark:text-gray-400 text-lg font-normal font-['DM_Sans'] leading-[23px]">
          Manage your personal preferences and team settings.
        </p>
      </div>
      
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow p-6">
        <div className="mb-8">
          <h2 className="text-[#40424A] dark:text-gray-100 text-xl font-medium font-['DM_Sans'] mb-6">Personal Settings</h2>
          
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <p className="text-[#40424A] dark:text-gray-100 text-base font-medium">When should Usermuse join & record a meeting?</p>
                <p className="text-slate dark:text-gray-400 text-sm">Select for your personal meetings.</p>
              </div>
              
              <div className="w-full md:w-64">
                <Select 
                  value={meetingJoinPreference}
                  onValueChange={handleMeetingJoinPreferenceChange}
                  disabled={preferencesLoading || !activeWorkspace?.id}
                >
                  <SelectTrigger className="w-full dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100">
                    <SelectValue>
                      {getSelectedOption() && (
                        <div className="flex items-center">
                          {getSelectedOption()?.icon}
                          <span>{getSelectedOption()?.label}</span>
                        </div>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                    {meetingJoinOptions.map((option) => (
                      <SelectItem 
                        key={option.value} 
                        value={option.value}
                        className="cursor-pointer focus:bg-pink-50 dark:focus:bg-gray-700 dark:text-gray-100"
                      >
                        <div className="flex flex-col w-full">
                          <div className="flex items-center">
                            {option.icon}
                            <span className="font-medium">{option.label}</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">{option.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 